import React from 'react';
import { i18n } from 'config/locales';
import { Summary, NameSummary } from 'components/Summary';

import { FOREIGN, FOREIGN_CONTACTS } from 'config/formSections/foreign';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { foreignContactsAddressNameFields } from 'constants/enums/people';
import ErrorMessage from 'components/ErrorMessage';
import Field from 'components/Form/Field';
import { isPVQForm } from 'helpers/form';
import ForeignNational from './ForeignNational';
import SpouseForeignNational from './SpouseForeignNational';

const sectionConfig = {
  key: FOREIGN_CONTACTS.key,
  section: FOREIGN.name,
  store: FOREIGN.store,
  subsection: FOREIGN_CONTACTS.name,
  storeKey: FOREIGN_CONTACTS.storeKey,
};

export class Contacts extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.handleError = this.handleError.bind(this);
  }

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      HasForeignContacts: this.props.HasForeignContacts,
      List: this.props.List,
      HasSpouseForeignContacts: this.props.HasSpouseForeignContacts,
      SpouseForeignContactsList: this.props.SpouseForeignContactsList,
      ...queue,
    });
  };

  updateHasForeignContacts = (values) => {
    this.update({
      HasForeignContacts: values,
      List: values.value === 'Yes' ? this.props.List : [],
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateHasSpouseForeignContacts = (values) => {
    this.update({
      HasSpouseForeignContacts: values,
      SpouseForeignContactsList: values.value === 'Yes' ? this.props.SpouseForeignContactsList : [],
    });
  };

  updateSpouseForeignContactsList = (values) => {
    this.update({
      SpouseForeignContactsList: values,
    });
  };

  summary = (item, index) => {
    const obj = (item || {}).Item || {};
    const name = NameSummary(obj.Name);

    return Summary({
      type: i18n.t('foreign.contacts.collection.summary.item'),
      index,
      left: name,
      placeholder: i18n.t('foreign.contacts.collection.summary.unknown'),
    });
  };

  render() {
    const { errors, peopleWhoKnowYou, List } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);

    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(List?.items, peopleWhoKnowYou, foreignContactsAddressNameFields);

    const isPVQ = isPVQForm(this.props.formType);

    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    return isPVQ ? (
      <div
        className="section-content foreign-contacts"
        data-section={FOREIGN.key}
        data-subsection={FOREIGN_CONTACTS.key}
      >
        <h1 className="section-header">{i18n.t('foreign.pvq.subsection.contacts')}</h1>
        <Field optional className="no-margin-bottom">
          <p>{i18n.t('foreign.pvq.contacts.para.intro1')}</p>
          <p>{i18n.t('foreign.pvq.contacts.para.intro2')}</p>
          <ul>
            <li>{i18n.t('foreign.pvq.contacts.para.introItem1')}</li>
            <li>{i18n.t('foreign.pvq.contacts.para.introItem2')}</li>
            <li>{i18n.t('foreign.pvq.contacts.para.introItem3')}</li>
            <li>{i18n.t('foreign.pvq.contacts.para.introItem4')}</li>
            <li>{i18n.t('foreign.pvq.contacts.para.introItem5')}</li>
            <li>{i18n.t('foreign.pvq.contacts.para.introItem6')}</li>
          </ul>
        </Field>

        <Branch
          name="has_foreign_contacts"
          label={i18n.t('foreign.pvq.contacts.heading.title')}
          labelSize="h4"
          {...this.props.HasForeignContacts}
          warning
          onUpdate={this.updateHasForeignContacts}
          onError={this.handleError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          attribute="HasForeignContacts"
        >
          <>
            <ul>
              <li>{i18n.t('foreign.pvq.contacts.heading.titleItem1')}</li>
              <li>{i18n.t('foreign.pvq.contacts.heading.titleItem2')}</li>
              <li>{i18n.t('foreign.pvq.contacts.heading.titleItem3')}</li>
              <li>{i18n.t('foreign.pvq.contacts.heading.titleItem4')}</li>
            </ul>
            <p>{i18n.t('foreign.pvq.contacts.heading.titleNote')}</p>
          </>
        </Branch>
        {this.props.HasForeignContacts?.value === 'Yes'
        && (
        <Accordion
          {...this.props.List}
          defaultState={this.props.defaultState}
          scrollToBottom={this.props.scrollToBottom}
          onUpdate={this.updateList}
          onError={this.handleError}
          errors={accordionErrors}
          summary={this.summary}
          description={i18n.t('foreign.pvq.contacts.collection.summary.title')}
          appendTitle={i18n.t('foreign.pvq.contacts.collection.appendTitle')}
          appendMessage={i18n.m('foreign.pvq.contacts.collection.appendMessage')}
          appendLabel={i18n.t('foreign.contacts.collection.append')}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          attribute="List"
          additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          addendumExactAttribute
        >
          <ForeignNational
            name="Item"
            applicantBirthdate={this.props.applicantBirthdate}
            bind
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute="List"
            formType={this.props.formType}
          />
        </Accordion>
        )}

        <Branch
          name="has_spouse_foreign_contacts"
          label={i18n.t('foreign.pvq.spouseContacts.heading.title')}
          labelSize="h4"
          {...this.props.HasSpouseForeignContacts}
          warning
          onUpdate={this.updateHasSpouseForeignContacts}
          onError={this.handleError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          attribute="HasSpouseForeignContacts"
        />
        {this.props.HasSpouseForeignContacts?.value === 'Yes'
          && (
            <Accordion
              {...this.props.SpouseForeignContactsList}
              defaultState={this.props.defaultState}
              scrollToBottom={this.props.scrollToBottom}
              onUpdate={this.updateSpouseForeignContactsList}
              onError={this.handleError}
              errors={accordionErrors}
              summary={this.summary}
              description={i18n.t('foreign.pvq.spouseContacts.collection.summary.title')}
              appendTitle={i18n.t('foreign.pvq.spouseContacts.collection.appendTitle')}
              appendLabel={i18n.t('foreign.pvq.spouseContacts.collection.append')}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="SpouseForeignContactsList"
              additionalErrors={
                (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
                || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
              }
            >
              <SpouseForeignNational
                name="Item"
                applicantBirthdate={this.props.applicantBirthdate}
                bind
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                attribute="SpouseForeignContactsList"
                formType={this.props.formType}
              />
            </Accordion>
          )}
      </div>
    ) : (
      <div
        className="section-content foreign-contacts"
        data-section={FOREIGN.key}
        data-subsection={FOREIGN_CONTACTS.key}
      >
        <h1 className="section-header">{i18n.t('foreign.destination.contacts')}</h1>
        <Branch
          name="has_foreign_contacts"
          label={i18n.t('foreign.contacts.heading.title')}
          labelSize="h4"
          {...this.props.HasForeignContacts}
          warning
          onUpdate={this.updateHasForeignContacts}
          onError={this.handleError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          attribute="HasForeignContacts"
        >
          {i18n.t('foreign.contacts.para.definition')}
          {i18n.t('foreign.contacts.para.includes')}
        </Branch>
        <Show when={this.props.HasForeignContacts.value === 'Yes'}>
          <Accordion
            {...this.props.List}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            onUpdate={this.updateList}
            onError={this.handleError}
            errors={accordionErrors}
            summary={this.summary}
            description={i18n.t('foreign.contacts.collection.summary.title')}
            appendTitle={i18n.t('foreign.contacts.collection.appendTitle')}
            appendMessage={i18n.m('foreign.contacts.collection.appendMessage')}
            appendLabel={i18n.t('foreign.contacts.collection.append')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute="List"
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <ForeignNational
              name="Item"
              applicantBirthdate={this.props.applicantBirthdate}
              bind
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="List"
              requireForeignContactAPOFPOIDK={this.props.requireForeignContactAPOFPOIDK}
            />
          </Accordion>
        </Show>
      </div>
    );
  }
}

Contacts.defaultProps = {
  HasForeignContacts: {},
  List: {},
  HasSpouseForeignContacts: {},
  SpouseForeignContactsList: {},
  onUpdate: () => { },
  onError: (value, arr) => arr,
  section: 'foreign',
  subsection: 'contacts',
  addressBooks: {},
  dispatch: () => { },
  defaultState: true,
  scrollToBottom: '',
  errors: [],
};

export default connectSubsection(Contacts, sectionConfig);
